import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

const whatIfRelated: RL[] = [
  {
    title: "SAINT® vs SPRAVATO®",
    href: "/learn/saint-vs-spravato",
    description: "How SAINT® compares to SPRAVATO® (esketamine) for depression",
  },
  {
    title: "SAINT® vs Ketamine",
    href: "/learn/saint-vs-ketamine",
    description: "How SAINT® compares to ketamine on evidence, time, and cost",
  },
  {
    title: "What Happens After SAINT® TMS?",
    href: "/learn/what-happens-after-saint-tms",
    description:
      "Recovery, follow-up, and what to expect after the treatment week",
  },
  {
    title: "SAINT® TMS Success Rates",
    href: "/learn/saint-tms-success-rates",
    description: "Remission rates and response data from the SAINT® trials",
  },
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "What Is Treatment-Resistant Depression?",
    href: "/learn/what-is-treatment-resistant-depression",
    description: "Definition, causes, and treatment options",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "What percentage of people don't respond to SAINT®?",
    answer:
      "In the 2022 randomized controlled trial, 79% of participants reached remission, meaning about 21% did not (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. Not reaching remission after one course does not mean no treatment will work — it means the team reviews your response and discusses next steps, which may include a repeat course or a different treatment type offered at the same clinic.",
  },
  {
    question: "Can SAINT® be repeated?",
    answer:
      "A repeat SAINT® course may be an option if your initial response is partial or if relief wears off over time. Whether a repeat course is appropriate depends on your individual response, your symptoms, and your psychiatrist's assessment. The treatment team can discuss whether repeating the five-day protocol makes sense for you or whether a different approach would be better. SAINT® at NWCOA starts at $25,000; coverage varies by plan and we verify your benefits for free.",
  },
  {
    question: "What do you try next if SAINT® doesn't work?",
    answer:
      "If SAINT® doesn't produce enough relief, the next steps may include SPRAVATO® (esketamine), ketamine, medication management, or psychotherapy — all offered at the same clinic. The team reviews your response, your symptoms, and your history to recommend the best next option. You don't get sent to another provider; the same team that knows your case continues your care.",
  },
  {
    question: "How long before I know if SAINT® worked?",
    answer:
      "Some people feel better within the five-day treatment week. Others notice improvement in the days or weeks after the week ends — the brain can continue to adjust after stimulation stops. The treatment team tracks your mood with standardized scales during follow-up so they can tell the difference between a delayed response and a need for a different approach. Don't assume the treatment failed on day five.",
  },
  {
    question: "Does insurance cover the next steps if SAINT® doesn't work?",
    answer:
      "Coverage for follow-up treatments varies by plan and by treatment type. SPRAVATO®, ketamine, medication management, and psychotherapy each have their own insurance pathway. We verify your benefits for free, whatever carrier you have, so you know what to expect before you commit. Coverage may be available; it depends on your plan.",
  },
  {
    question: "Does not responding to SAINT® mean nothing will work?",
    answer:
      "No. Not responding to one treatment does not mean depression is untreatable. Depression treatment is often a process of finding the right approach for each person. The advantage of a clinic that offers SAINT®, SPRAVATO®, ketamine, medication management, and psychotherapy is that the team can move to the next option without sending you elsewhere. Your psychiatrist reviews your full history and response before recommending what to try next.",
  },
];

export const pageWhatIfSaintDoesntWork = makeArticle({
  slug: "what-if-saint-doesnt-work",
  title: "What If SAINT® Doesn't Work? Next Steps & Options",
  description:
    "Not everyone remits after SAINT®. What happens next: follow-up, repeat courses, Spravato, ketamine and other options under one roof.",
  canonical: "/learn/what-if-saint-doesnt-work",
  h1: "What If SAINT® Doesn't Work for Me?",
  breadcrumbLabel: "What If SAINT® Doesn't Work?",
  shortAnswer:
    "SAINT® does not work for everyone. In the 2022 randomized trial, 79% of participants reached remission — about 21% did not (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. If you don't respond, the next steps may include continued follow-up, a repeat SAINT® course, or other treatments — SPRAVATO®, ketamine, medication management, and psychotherapy — all available at the same clinic. If you are in crisis, call or text 988.",
  keyTakeaways: [
    "In the 2022 SAINT® trial, 79% of participants reached remission — about 21% did not (Cole et al., 2022). Individual results may vary.",
    "Some people improve in the days or weeks after the treatment week, not just during it.",
    "Not reaching remission after one course does not mean no treatment will work.",
    "SPRAVATO®, ketamine, medication management, and psychotherapy are all offered at the same clinic.",
    "Coverage varies by plan; we verify your benefits for free.",
    "If you are in crisis, call or text 988.",
  ],
  sections: [
    {
      heading: "Quick Answer: What If SAINT® Doesn't Work?",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is FDA-cleared for major depressive disorder (MDD) in adults whose depression has not improved enough on prior antidepressant medication. But SAINT® does not work for everyone. In the 2022 randomized controlled trial, 79% of participants reached remission — which means about 21% did not (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. Not reaching remission after one course is not the end of the road.",
        "If SAINT® doesn't produce enough relief, the next steps may include continued follow-up, a repeat SAINT® course, or other treatments — all available at the same clinic. NWCOA, a Verified SAINT® Provider in Coral Springs, Florida, offers SPRAVATO® (esketamine), ketamine, medication management, and psychotherapy alongside SAINT®, so you don't get sent to another provider. [[Learn more about SAINT®|/saint]] or [[check if you're a candidate|/am-i-a-candidate-screener]].",
      ],
    },
    {
      heading: 'What "Not Working" Actually Means',
      paragraphs: [
        "Remission means your depression scores drop below a certain threshold on a standardized mood scale. Not reaching remission after one SAINT® course does not mean you got no benefit at all. Some people improve partially — their symptoms ease but don't fully resolve. Others don't notice a change. The treatment team uses standardized scales to measure your response objectively, not just how you feel on a given day.",
        "The 79% remission figure from the 2022 trial is encouraging, but it also means that about 1 in 5 participants didn't reach that threshold with one course (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. That's why the follow-up plan and the range of next-step options matter. A partial response or no response is a signal to adjust the plan, not a reason to give up.",
      ],
    },
    {
      heading: "Delayed Responders: Improvement After the Treatment Week",
      paragraphs: [
        "Not everyone who responds to SAINT® feels better by the end of the fifth day. Some people notice improvement in the days or weeks after the treatment week ends. The brain continues to adjust after the stimulation stops, and mood can continue to lift even after the sessions are over.",
        "This is why the follow-up period is part of the protocol, not an afterthought. The treatment team tracks your mood with standardized scales during the weeks after SAINT® so they can tell the difference between a delayed response and a need for a different approach.",
        "If you reach the end of the week and don't feel different, don't assume the treatment failed. The team monitors you closely and helps you understand what to expect next. A delayed response is not uncommon, and the follow-up visits are designed to catch it. [[See what happens after SAINT®|/learn/what-happens-after-saint-tms]].",
      ],
    },
    {
      heading: "The Follow-Up Plan After SAINT®",
      paragraphs: [
        "After the five-day SAINT® week, the treatment team schedules follow-up visits to track your response. They use standardized mood scales — the same kinds of scales used in the clinical trials — to measure how your symptoms change over days and weeks.",
        "If you're not improving, the team reviews your symptoms and discusses next steps. This might mean adjusting your current medication, considering a repeat SAINT® course, or exploring a different treatment type. The follow-up plan is a checkpoint, not a dead end. Your psychiatrist looks at the full picture — your mood scores, your side effects, your overall function — before recommending a change.",
        "The team also watches for any side effects or changes in mood during follow-up. If your mood drops or you have thoughts of suicide, tell the team right away. If you are in crisis, call or text 988. [[Verify your insurance benefits|/check-insurance-options]] if you're considering next steps.",
      ],
    },
    {
      heading: "Can SAINT® Be Repeated?",
      paragraphs: [
        "A repeat SAINT® course may be an option if your initial response is partial — you improved but didn't reach remission — or if relief wears off over time. Whether a repeat course makes sense depends on your individual response, your symptoms, and your psychiatrist's assessment.",
        "The five-day protocol stays the same, but the targeting may be adjusted based on your response to the first course. Your psychiatrist reviews your mood scores and your overall progress before recommending a repeat. SAINT® at NWCOA starts at $25,000; coverage varies by plan and we verify your benefits for free. [[Check your eligibility|/am-i-a-candidate-screener]] to see if a repeat course is an option for you.",
      ],
    },
    {
      heading: "Options Under One Roof: What Comes Next",
      paragraphs: [
        "One advantage of getting SAINT® at NWCOA is that if it doesn't produce enough relief, you don't get sent to another clinic. SPRAVATO® (esketamine), ketamine, medication management, and psychotherapy are all offered in the same place, by the same team that already knows your history. You don't start over with a new provider — you continue with the team that treated you.",
        "SPRAVATO® (esketamine) is an FDA-approved nasal spray for treatment-resistant depression in adults. It's administered in-office under the SPRAVATO® REMS program, typically over several weeks of scheduled visits. [[Compare SAINT® and SPRAVATO®|/learn/saint-vs-spravato]].",
        "Ketamine is another option. IV ketamine for depression is used off-label — the FDA has not approved it specifically for depression, but doctors may use it based on clinical evidence. It's typically given as a series of infusions over a few weeks. [[Compare SAINT® and ketamine|/learn/saint-vs-ketamine]].",
        "Medication management is ongoing. Your psychiatrist can adjust your current medications, try new ones, or fine-tune your regimen based on how you responded to SAINT®. Sometimes a medication change after SAINT® makes a meaningful difference, especially if the treatment uncovered a new pattern in your symptoms.",
        "Psychotherapy — including cognitive behavioral therapy and other approaches — can be used alongside any of these treatments or on its own. It's often part of a broader plan for managing depression long-term, especially after an acute treatment like SAINT®.",
        "Each option has its own evidence, timeline, and insurance pathway. Coverage varies by plan. We verify your benefits for free, whatever carrier you have. [[Learn about treatment-resistant depression|/learn/what-is-treatment-resistant-depression]] to understand where each option fits.",
      ],
    },
    {
      heading: "Next Steps If SAINT® Doesn't Produce Enough Relief",
      table: {
        headers: [
          "Next-step option",
          "When it's considered",
          "Time",
          "Coverage",
        ],
        rows: [
          [
            "Continued follow-up",
            "After the treatment week",
            "Days to weeks",
            "Included in the SAINT® program",
          ],
          [
            "Repeat SAINT® course",
            "If initial response is partial or wears off",
            "5 days",
            "May be covered; coverage varies by plan",
          ],
          [
            "SPRAVATO® (esketamine)",
            "If SAINT® doesn't produce enough relief",
            "Weeks to months",
            "May be covered; coverage varies by plan",
          ],
          [
            "Ketamine",
            "If SAINT® doesn't produce enough relief",
            "Varies by protocol",
            "May be covered; coverage varies by plan",
          ],
          [
            "Medication management",
            "Ongoing, alongside or after any treatment",
            "Ongoing",
            "May be covered; coverage varies by plan",
          ],
          [
            "Psychotherapy",
            "Alongside any treatment",
            "Ongoing",
            "May be covered; coverage varies by plan",
          ],
        ],
      },
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: whatIfRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "October 2026",
    lastReviewed: "October 2026",
    lastUpdated: "October 2026",
  },
  toc: [
    {
      label: "Quick Answer",
      href: "#quick-answer-what-if-saint-doesnt-work",
    },
    {
      label: 'What "Not Working" Means',
      href: "#what-not-working-actually-means",
    },
    {
      label: "Delayed Responders",
      href: "#delayed-responders-improvement-after-the-treatment-week",
    },
    {
      label: "The Follow-Up Plan",
      href: "#the-follow-up-plan-after-saint",
    },
    {
      label: "Can SAINT® Be Repeated?",
      href: "#can-saint-be-repeated",
    },
    {
      label: "Options Under One Roof",
      href: "#options-under-one-roof-what-comes-next",
    },
    {
      label: "Next Steps Table",
      href: "#next-steps-if-saint-doesnt-produce-enough-relief",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
